<script lang="ts">
	import { Chevron, ChevronDirection, ChipGroup, FilterBox } from '@repo/commons/components'

	interface Props {
		filters?: Record<string, string[] | boolean>
		itemCounts?: Record<string, number[]>
		selectedChips?: string[]
		allowReordering?: boolean
		onreorder?: ((key: string, newValue: string[]) => void) | null
	}

	let {
		filters = {},
		itemCounts = {},
		selectedChips: selectedChipsWritable = $bindable<string[]>([]),
		allowReordering = false,
		onreorder = null
	}: Props = $props()

	// Local UI state to toggle visibility of filter groups
	let expandedFilters = $state<Record<string, boolean>>({})

	// Tracks checkbox state for each filter item
	let checkboxStatesWritable = $state<Record<string, Record<string, boolean>>>({})

	function toggleExpand(filterKey: string): void {
		expandedFilters[filterKey] = !expandedFilters[filterKey]
	}

	// Handle checkbox state change in FilterBox
	const handleFilterChanged = (key: string, value: string, checked: boolean): void => {
		const chipText = `${key}=${value}`

		if (checked) {
			if (!selectedChipsWritable.includes(chipText)) {
				selectedChipsWritable.push(chipText)
			}
		} else {
			selectedChipsWritable = selectedChipsWritable.filter((chip) => chip !== chipText)
		}
	}

	// Handle chip removal and update checkbox state accordingly
	const handleChipRemove = (item: string): void => {
		const [key, value] = item.split('=')
		selectedChipsWritable = selectedChipsWritable.filter((chip) => chip !== item)
		checkboxStatesWritable[key] ??= {}
		checkboxStatesWritable[key][value] = false
	}
</script>

<div class="filter-group">
	<ChipGroup
		selectedItems={selectedChipsWritable}
		onRemove={handleChipRemove}
		placeholderText="No filters selected"
	/>

	{#each Object.entries(filters) as [key, values]}
		{#if typeof values === 'boolean'}
			<div class="filter-item">
				<div class="filter-header" onclick={() => toggleExpand(key)}>
					<Chevron
						direction={expandedFilters[key] ? ChevronDirection.DOWN : ChevronDirection.RIGHT}
					/>
					<span class="filter-label">{key}</span>
				</div>
				<FilterBox
					{allowReordering}
					{key}
					booleanFilter={true}
					{checkboxStatesWritable}
					onfilterChanged={handleFilterChanged}
					{onreorder}
				/>
			</div>
		{:else}
			<div class="filter-item">
				<div class="filter-header" onclick={() => toggleExpand(key)}>
					<Chevron
						direction={expandedFilters[key] ? ChevronDirection.DOWN : ChevronDirection.RIGHT}
					/>
					<span class="filter-label">{key}</span>
				</div>
				{#if expandedFilters[key]}
					<FilterBox
						{allowReordering}
						{key}
						{values}
						counts={itemCounts[key] ?? []}
						{checkboxStatesWritable}
						onfilterChanged={handleFilterChanged}
						{onreorder}
					/>
				{/if}
			</div>
		{/if}
	{/each}
</div>

<style>
	.filter-group {
		@apply space-y-4;
	}

	.filter-item {
		@apply flex flex-col p-4 border rounded-lg;
	}

	.filter-header {
		@apply flex items-center cursor-pointer border-b;
		padding: 10px 0;
	}

	.filter-label {
		@apply ml-2 text-lg;
	}
</style>
